import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Adgangskoderne matcher ikke");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Adgangskoden skal være mindst 6 tegn");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      toast.success(response.data.message);
      setSuccess(true);
      
      // Redirect til login efter 3 sekunder
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved nulstilling af adgangskode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" 
         style={{backgroundImage: 'url(https://images.unsplash.com/photo-1761477423581-c470874aef50?q=80&w=2000&auto=format&fit=crop)', 
                backgroundSize: 'cover', backgroundPosition: 'center'}}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      
      <div className="relative w-full max-w-md">
        <div className="bg-black/80 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl p-8">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til login
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <Lock className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">
              Nulstil adgangskode
            </h1>
          </div>

          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h2 className="text-xl font-bold text-green-400">Adgangskode nulstillet!</h2>
              <p className="text-muted-foreground text-sm">
                Du omdirigeres til login om få sekunder...
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary hover:underline"
                data-testid="go-to-login-link"
              >
                <ArrowLeft className="h-4 w-4" />
                Gå til login nu
              </Link>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground text-sm mb-6">
                Indtast din nye adgangskode nedenfor.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                {!searchParams.get("token") && (
                  <div className="space-y-2">
                    <Label htmlFor="token" className="text-xs uppercase tracking-wider font-mono">
                      Reset Token
                    </Label>
                    <Input
                      id="token"
                      data-testid="reset-token-input"
                      type="text"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      required
                      placeholder="Indsæt dit reset-token"
                      className="bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary h-10 font-mono"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-xs uppercase tracking-wider font-mono">
                    Ny adgangskode
                  </Label>
                  <Input
                    id="newPassword"
                    data-testid="new-password-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Mindst 6 tegn"
                    className="bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider font-mono">
                    Bekræft adgangskode
                  </Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Gentag adgangskoden"
                    className="bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary h-10 font-mono"
                  />
                </div>

                <Button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_10px_rgba(225,255,47,0.2)] font-semibold uppercase tracking-wide text-xs h-10 rounded-md transition-all active:scale-95"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {loading ? "Nulstiller..." : "Nulstil adgangskode"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
